import React from 'react';
import {
    AdvancedTableHeader,
    AdvancedTableData,
    Card,
    SimpleTable,
    MultiRowColumn,
    MultiSelectSearch,
    DefaultMultiSelectSearch,
    IExamsList,
    AccreditationCenterDetailsModal,
} from 'components';
import { useLocalErrorHandler, useMultiSearch } from 'hooks';
import { formatFullAddress, formatQualification } from '_helpers';
import { IAccreditationCentersFilters, IQualificationsFilters } from 'components/reduxModels';
import {
    IAccreditationCenter,
    IAccreditationCenterDetails,
    IAccreditationCenters,
    IApiError,
    IExamsFilters,
    IQualificationInfoList, IRegion,
} from 'components/models';
import { ErrorHandlerType } from 'components/enums';

const AccreditationCentersListContainer: React.FC<Props> = (
  {
      setErrorHandler,
      getAccreditationCenters,
      apiError,
      accreditationCentersList,
      getQualifications,
      qualifications,
      ...props
  },
) => {
    useLocalErrorHandler(setErrorHandler);
    const [selectedId, selectId] = React.useState<React.ReactText | null>(null);
    const { filters, changeFilterField } = useMultiSearch<IAccreditationCentersFilters>(getAccreditationCenters, {});

    const tableHeaders: AdvancedTableHeader[] = [
        { fieldName: '', title: 'ПЛОЩАДКА И АДРЕС', columnStyles: { width: '100%' } },
        { fieldName: '', title: 'РЕГИОН', columnStyles: { minWidth: '11.25rem', whiteSpace: 'nowrap' } },
    ];

    if (props.isAsc) {
        tableHeaders[1].title = 'ВМЕСТИМОСТЬ (ЧЕЛ/ДЕНЬ)';
    }

    React.useEffect(() => {
        if (props.clearAccreditationCenterDetails) props.clearAccreditationCenterDetails();
        getAccreditationCenters(filters);
        getQualifications({ pageSize: 500 });
        if (props.getRegions) props.getRegions();
    }, []);

    React.useEffect(() => {
        if (selectedId) {
            props.getAccreditationCenter(String(selectedId));
        }
    }, [selectedId]);

    const getAudience = (accreditationCenter: IAccreditationCenter) => {
        const qualificationInfo = accreditationCenter.qualifications.find(q => q.id === props.qualificationId);
        if (qualificationInfo) return qualificationInfo.audienceCapacity;

        return '-';
    };

    const tableData = React.useMemo((): AdvancedTableData[] => (accreditationCentersList ? accreditationCentersList.items.map(item => ({
        id: item.id,
        columns: [
            <MultiRowColumn
              rows={[
                  item.name,
                  formatFullAddress(item),
              ]}
            />,
            props.isAsc ? getAudience(item) : item.regionName,
        ],
    })) : []), [accreditationCentersList]);

    return (
      <Card
        title="Аккредитационные площадки"
        category="Список всех аккредитационных площадок, доступных для проведения мероприятий"
        cardClassName="position-relative"
        apiError={apiError}
        withoutBorder
        withoutPadding
      >
          <MultiSelectSearch hide={props.isAsc}>
              <DefaultMultiSelectSearch
                label="СПЕЦИАЛЬНОСТЬ"
                options={qualifications ? qualifications.items.map(q => ({
                    label: formatQualification(q),
                    value: q.id,
                })) : []}
                placeholder="Выберите одну или несколько специальностей"
                onChange={changeFilterField('qualificationIds')}
              />
              {props.isMain && (
                <DefaultMultiSelectSearch
                  label="РЕГИОН"
                  options={props.regions ? props.regions.map(r => ({
                      label: r.name,
                      value: String(r.id),
                  })) : []}
                  placeholder="Выберите один или несколько регионов"
                  onChange={changeFilterField('regionsId')}
                />
              )}
          </MultiSelectSearch>
          <SimpleTable
            tableCellClassName="cursor-pointer"
            tableClassName="mb-5"
            tableHeaders={tableHeaders}
            tableRows={tableData}
            handleRowClick={selectId}
            placeholderTitle="Нет аккредитационных площадок в списке"
            placeholderText="Список аккредитационных площадок недоступен"
          />

          <AccreditationCenterDetailsModal
            show={!!selectedId}
            onHide={() => {
                selectId(null);
            }}
            isAsc={props.isAsc}
            qualificationId={props.qualificationId}
            accreditationCenterDetails={props.accreditationCenterDetails}
            getExams={props.getExams}
            exams={props.exams}
            accreditationCenterQualifications={props.accreditationCenterQualifications}
            getAccreditationCenterQualifications={props.getAccreditationCenterQualifications}
            clearAccreditationCenterDetails={props.clearAccreditationCenterDetails}
            clearExams={props.clearExams}
            clearAccreditationCenterQualifications={props.clearAccreditationCenterQualifications}
          />
      </Card>
    );
};

export default AccreditationCentersListContainer;

interface Props {
    accreditationCentersList: IAccreditationCenters | null;
    accreditationCenterDetails: IAccreditationCenterDetails | null;
    qualifications: IQualificationInfoList | null;
    accreditationCenterQualifications?: IQualificationInfoList | null;
    exams: IExamsList | null;
    regions?: IRegion[] | null;
    apiError: IApiError | null;
    isAsc?: boolean;
    qualificationId?: string;
    isMain?: boolean;

    /* Functions */
    getQualifications: (payload: IQualificationsFilters) => Promise<void>;
    getAccreditationCenterQualifications?: (payload: IQualificationsFilters) => Promise<void>;
    getAccreditationCenter: (payload: string) => Promise<void>;
    getExams: (payload: IExamsFilters) => Promise<void>;
    getRegions?: () => Promise<void>;
    clearExams: () => void;
    clearAccreditationCenterQualifications?: () => void;
    clearAccreditationCenterDetails?: () => void;
    getAccreditationCenters: (payload: IAccreditationCentersFilters) => Promise<void>;
    setErrorHandler: (handler: ErrorHandlerType) => void;
}
